/**
 * ProcessSection
 *
 * Scroll-driven switcher for `.front-process-section`.
 *
 * The section is `N * 100vh` tall and its `.container` is sticky, so the scroll
 * distance while the container is pinned (`section height - container height`)
 * is split into N equal ranges: one range = one step.
 *
 * - All devices : toggles `_active` on images, content items and list items.
 * - Desktop only: moves `.process-bar` (via `--p`, in px) continuously with scroll
 *                 and makes `.process-list-item` clickable / focusable.
 * - Mobile      : only class switching (the bar is cleared, list items are segments).
 *
 * @package AGDS\Theme
 * @version 1.0.0
 * @author Andy Groove
 */

const DEFAULTS = {
	activeClass: '_active',
	// Must match the SCSS `lg` breakpoint (min-width side).
	desktopQuery: '(min-width: 992px)',
	// Optional custom scroller (e.g. Lenis): ( y, { immediate } ) => void.
	scrollTo: null,
	selectors: {
		stage:    '.container',
		images:   '.process-images-wrapper .image-wrap',
		contents: '.process-content-item',
		listWrap: '.process-list-wrapper',
		bar:      '.process-bar',
		items:    '.process-list-item',
	},
};

const instances = new WeakMap();

export default class ProcessSection {

	/**
	 * Initializes all sections matching the selector.
	 *
	 * @param {string} selector
	 * @param {Object} options
	 * @return {ProcessSection[]}
	 */
	static init( selector = '.front-process-section', options = {} ) {
		return [ ...document.querySelectorAll( selector ) ]
			.map( ( el ) => instances.get( el ) || new ProcessSection( el, options ) )
			.filter( ( instance ) => instance.isValid );
	}

	/**
	 * @param {HTMLElement} root
	 * @param {Object}      options
	 */
	constructor( root, options = {} ) {
		this.isValid = false;

		if ( ! ( root instanceof HTMLElement ) ) {
			return;
		}

		this.root = root;
		this.opts = {
			...DEFAULTS,
			...options,
			selectors: { ...DEFAULTS.selectors, ...( options.selectors || {} ) },
		};

		const s = this.opts.selectors;

		this.stage    = root.querySelector( s.stage );
		this.listWrap = root.querySelector( s.listWrap );
		this.bar      = root.querySelector( s.bar );
		this.images   = [ ...root.querySelectorAll( s.images ) ];
		this.contents = [ ...root.querySelectorAll( s.contents ) ];
		this.items    = [ ...root.querySelectorAll( s.items ) ];
		this.count    = this.items.length;

		if ( ! this.stage || ! this.listWrap || this.count < 1 ) {
			return;
		}

		if ( this.images.length !== this.count || this.contents.length !== this.count ) {
			console.warn( '[ProcessSection] Images, content and list items counts differ.', root );
		}

		this.index       = -1;
		this.distance    = 0;
		this.stops       = [];
		this.barValue    = '';
		this.rafId       = 0;
		this.listening   = false;
		this.mq          = window.matchMedia( this.opts.desktopQuery );
		this.reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );
		this.isDesktop   = this.mq.matches;

		this.update   = this.update.bind( this );
		this.schedule = this.schedule.bind( this );
		this.onClick  = this.onClick.bind( this );
		this.onKey    = this.onKey.bind( this );
		this.onMedia  = this.onMedia.bind( this );

		this.bind();
		this.applyMode();
		this.measure();
		this.update();

		instances.set( root, this );
		this.isValid = true;
	}

	/* ---------------------------------------------------------------------
	 * Setup.
	 * ------------------------------------------------------------------ */

	bind() {
		this.listWrap.addEventListener( 'click', this.onClick );
		this.listWrap.addEventListener( 'keydown', this.onKey );

		if ( this.mq.addEventListener ) {
			this.mq.addEventListener( 'change', this.onMedia );
		} else {
			this.mq.addListener( this.onMedia ); // Safari < 14.
		}

		// Re-measure when the section or the list changes size (resize, fonts, i18n).
		if ( 'ResizeObserver' in window ) {
			this.resizeObserver = new ResizeObserver( () => {
				this.measure();
				this.schedule();
			} );
			this.resizeObserver.observe( this.root );
			this.resizeObserver.observe( this.listWrap );
		} else {
			window.addEventListener( 'resize', this.schedule );
		}

		// Listen to scroll only while the section is near the viewport.
		if ( 'IntersectionObserver' in window ) {
			this.intersectionObserver = new IntersectionObserver(
				( [ entry ] ) => this.toggleScroll( entry.isIntersecting ),
				{ rootMargin: '100% 0px 100% 0px' }
			);
			this.intersectionObserver.observe( this.root );
		} else {
			this.toggleScroll( true );
		}
	}

	toggleScroll( on ) {
		if ( on === this.listening ) {
			return;
		}

		this.listening = on;

		if ( on ) {
			window.addEventListener( 'scroll', this.schedule, { passive: true } );
			this.schedule();
		} else {
			window.removeEventListener( 'scroll', this.schedule );
		}
	}

	/**
	 * Desktop: items are interactive. Mobile: plain segments, no bar.
	 */
	applyMode() {
		this.items.forEach( ( item ) => {
			if ( this.isDesktop ) {
				item.setAttribute( 'tabindex', '0' );
			} else {
				item.removeAttribute( 'tabindex' );
			}
		} );

		if ( ! this.isDesktop ) {
			this.setBar( null );
		}
	}

	onMedia( e ) {
		this.isDesktop = e.matches;
		this.applyMode();
		this.measure();
		this.update();
	}

	/**
	 * Reads geometry. Called on resize, never on scroll.
	 */
	measure() {
		this.distance = Math.max( 0, this.root.offsetHeight - this.stage.offsetHeight );

		// Bar stops: top of every item relative to the list wrapper + wrapper bottom.
		const wrapTop = this.listWrap.getBoundingClientRect().top;

		this.stops = this.items.map( ( item ) => item.getBoundingClientRect().top - wrapTop );
		this.stops.push( this.listWrap.offsetHeight );
	}

	/* ---------------------------------------------------------------------
	 * Scroll.
	 * ------------------------------------------------------------------ */

	schedule() {
		if ( ! this.rafId ) {
			this.rafId = requestAnimationFrame( this.update );
		}
	}

	update() {
		this.rafId = 0;

		const top      = this.root.getBoundingClientRect().top;
		const raw      = this.distance > 0 ? -top / this.distance : 0;
		const progress = Math.min( 1, Math.max( 0, raw ) );
		const scaled   = progress * this.count;
		const index    = Math.min( this.count - 1, Math.floor( scaled ) );

		if ( index !== this.index ) {
			this.setActive( index );
		}

		if ( this.isDesktop ) {
			this.updateBar( index, Math.min( 1, scaled - index ) );
		}
	}

	setActive( index ) {
		const cls = this.opts.activeClass;

		this.index = index;

		[ this.images, this.contents, this.items ].forEach( ( list ) => {
			list.forEach( ( el, i ) => el.classList.toggle( cls, i === index ) );
		} );

		this.items.forEach( ( el, i ) => {
			if ( i === index ) {
				el.setAttribute( 'aria-current', 'step' );
			} else {
				el.removeAttribute( 'aria-current' );
			}
		} );
	}

	/**
	 * Bar height interpolates between the top of the active item and the top
	 * of the next one, so it moves smoothly with scroll and ends at the list bottom.
	 */
	updateBar( index, t ) {
		const from = this.stops[ index ];
		const to   = this.stops[ index + 1 ];

		if ( from === undefined || to === undefined ) {
			return;
		}

		this.setBar( from + ( to - from ) * t );
	}

	setBar( px ) {
		if ( ! this.bar ) {
			return;
		}

		const value = ( px === null ) ? '' : `${ px.toFixed( 1 ) }px`;

		if ( value === this.barValue ) {
			return;
		}

		this.barValue = value;

		if ( value ) {
			this.bar.style.setProperty( '--p', value );
		} else {
			this.bar.style.removeProperty( '--p' );
		}
	}

	/* ---------------------------------------------------------------------
	 * Navigation (desktop).
	 * ------------------------------------------------------------------ */

	onClick( e ) {
		if ( ! this.isDesktop ) {
			return;
		}

		const item = e.target.closest( this.opts.selectors.items );
		const i    = item ? this.items.indexOf( item ) : -1;

		if ( i > -1 ) {
			this.goTo( i );
		}
	}

	onKey( e ) {
		if ( ! this.isDesktop || ( e.key !== 'Enter' && e.key !== ' ' ) ) {
			return;
		}

		const item = e.target.closest( this.opts.selectors.items );
		const i    = item ? this.items.indexOf( item ) : -1;

		if ( i > -1 ) {
			e.preventDefault();
			this.goTo( i );
		}
	}

	/**
	 * Scrolls to the middle of the step range, so the target is unambiguous.
	 *
	 * @param {number} index
	 */
	goTo( index ) {
		if ( index < 0 || index >= this.count || index === this.index ) {
			return;
		}

		this.measure();

		const top       = this.root.getBoundingClientRect().top + window.scrollY;
		const y         = Math.round( top + ( ( index + 0.5 ) / this.count ) * this.distance );
		const immediate = this.reduceMotion.matches;

		if ( typeof this.opts.scrollTo === 'function' ) {
			this.opts.scrollTo( y, { immediate } );
		} else {
			window.scrollTo( { top: y, behavior: immediate ? 'auto' : 'smooth' } );
		}
	}

	/* ---------------------------------------------------------------------
	 * Cleanup.
	 * ------------------------------------------------------------------ */

	destroy() {
		if ( ! this.isValid ) {
			return;
		}

		cancelAnimationFrame( this.rafId );
		this.toggleScroll( false );

		this.resizeObserver && this.resizeObserver.disconnect();
		this.intersectionObserver && this.intersectionObserver.disconnect();
		window.removeEventListener( 'resize', this.schedule );

		this.listWrap.removeEventListener( 'click', this.onClick );
		this.listWrap.removeEventListener( 'keydown', this.onKey );

		if ( this.mq.removeEventListener ) {
			this.mq.removeEventListener( 'change', this.onMedia );
		} else {
			this.mq.removeListener( this.onMedia );
		}

		this.items.forEach( ( el ) => {
			el.removeAttribute( 'tabindex' );
			el.removeAttribute( 'aria-current' );
		} );
		this.setBar( null );

		instances.delete( this.root );
		this.isValid = false;
	}
}
